import Script from 'next/script';

type HtmlTagName = 'script' | 'style' | 'link' | 'meta';
type ScriptStrategy = 'afterInteractive' | 'lazyOnload';
type ParsedHtmlTag = {
  tagName: HtmlTagName;
  attrs: string;
  content: string;
};

// A alternativa de comentário HTML vem primeiro para que, ao começar em `<!--`,
// a regex consuma o comentário inteiro (incluindo qualquer <script>/<style>/etc.
// aninhado) em vez de extrair a tag de dentro dele. Assim, código comentado no
// campo de Scripts globais fica desativado. Um `<!--` que apareça dentro do
// conteúdo de um script legítimo continua sendo capturado como conteúdo do
// script, pois o match do `<script` começa antes.
const HTML_TAG_REGEX =
  /<!--[\s\S]*?-->|<script\b([^>]*)>([\s\S]*?)<\/script>|<style\b([^>]*)>([\s\S]*?)<\/style>|<link\b([^>]*)\/?\s*>|<meta\b([^>]*)\/?\s*>/gi;

const ATTRIBUTE_ALIASES: Record<string, string> = {
  'accept-charset': 'acceptCharset',
  as: 'as',
  charset: 'charSet',
  class: 'className',
  content: 'content',
  crossorigin: 'crossOrigin',
  fetchpriority: 'fetchPriority',
  for: 'htmlFor',
  href: 'href',
  'http-equiv': 'httpEquiv',
  id: 'id',
  integrity: 'integrity',
  itemprop: 'itemProp',
  media: 'media',
  name: 'name',
  nonce: 'nonce',
  property: 'property',
  referrerpolicy: 'referrerPolicy',
  rel: 'rel',
  sizes: 'sizes',
  src: 'src',
  type: 'type',
};

const BOOLEAN_ATTRIBUTES = new Set(['async', 'defer', 'disabled', 'nomodule']);

function normalizeAttributeName(name: string): string {
  const lowerCasedName = name.toLowerCase();

  if (lowerCasedName.startsWith('data-') || lowerCasedName.startsWith('aria-')) {
    return lowerCasedName;
  }

  if (ATTRIBUTE_ALIASES[lowerCasedName]) {
    return ATTRIBUTE_ALIASES[lowerCasedName];
  }

  return lowerCasedName.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function parseAttributes(attrs: string): Record<string, string | boolean> {
  const parsedAttributes: Record<string, string | boolean> = {};
  const attrRegex = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let match: RegExpExecArray | null;

  while ((match = attrRegex.exec(attrs)) !== null) {
    const rawName = match[1];
    const normalizedName = normalizeAttributeName(rawName);
    const attrValue = match[2] ?? match[3] ?? match[4];

    if (attrValue !== undefined) {
      parsedAttributes[normalizedName] = attrValue;
      continue;
    }

    if (rawName.toLowerCase() === 'crossorigin') {
      parsedAttributes.crossOrigin = 'anonymous';
      continue;
    }

    parsedAttributes[normalizedName] = BOOLEAN_ATTRIBUTES.has(rawName.toLowerCase()) ? true : '';
  }

  return parsedAttributes;
}

function parseHtmlTags(html: string): ParsedHtmlTag[] {
  const parsedTags: ParsedHtmlTag[] = [];
  let match: RegExpExecArray | null;

  while ((match = HTML_TAG_REGEX.exec(html)) !== null) {
    if (match[0].startsWith('<!--')) {
      continue;
    }

    if (match[1] !== undefined) {
      parsedTags.push({ tagName: 'script', attrs: match[1] ?? '', content: match[2] ?? '' });
      continue;
    }

    if (match[3] !== undefined) {
      parsedTags.push({ tagName: 'style', attrs: match[3] ?? '', content: match[4] ?? '' });
      continue;
    }

    if (match[5] !== undefined) {
      parsedTags.push({ tagName: 'link', attrs: match[5] ?? '', content: '' });
      continue;
    }

    parsedTags.push({ tagName: 'meta', attrs: match[6] ?? '', content: '' });
  }

  return parsedTags;
}

function renderScriptTag(
  tag: ParsedHtmlTag,
  key: string,
  strategy?: ScriptStrategy,
): JSX.Element | null {
  const scriptProps = parseAttributes(tag.attrs);

  if (!scriptProps.src && !tag.content.trim()) {
    return null;
  }

  if (strategy) {
    const scriptId =
      typeof scriptProps.id === 'string' && scriptProps.id.length > 0
        ? scriptProps.id
        : key;

    if (tag.content.trim()) {
      return (
        <Script key={key} id={scriptId} strategy={strategy} {...scriptProps}>
          {tag.content}
        </Script>
      );
    }

    return <Script key={key} strategy={strategy} {...scriptProps} />;
  }

  if (tag.content.trim()) {
    return (
      <script
        key={key}
        {...scriptProps}
        dangerouslySetInnerHTML={{ __html: tag.content }}
      />
    );
  }

  return <script key={key} {...scriptProps} />;
}

function renderTag(
  tag: ParsedHtmlTag,
  key: string,
  scriptStrategy?: ScriptStrategy,
): JSX.Element | null {
  const props = parseAttributes(tag.attrs);

  switch (tag.tagName) {
    case 'script':
      return renderScriptTag(tag, key, scriptStrategy);
    case 'style':
      if (!tag.content.trim()) return null;
      return <style key={key} {...props} dangerouslySetInnerHTML={{ __html: tag.content }} />;
    case 'link':
      if (!props.href) return null;
      return <link key={key} {...props} />;
    case 'meta':
      if (!Object.keys(props).length) return null;
      return <meta key={key} {...props} />;
    default:
      return null;
  }
}

function renderHtmlTags(
  html: string,
  prefix: string,
  scriptStrategy?: ScriptStrategy,
): JSX.Element[] {
  return parseHtmlTags(html)
    .map((tag, index) => renderTag(tag, `${prefix}-${tag.tagName}-${index}`, scriptStrategy))
    .filter((tag): tag is JSX.Element => tag !== null);
}

export function parseCustomHtmlTags(
  html: string,
  prefix: string = 'custom',
): JSX.Element[] {
  return renderHtmlTags(html, prefix);
}

export function parseFooterHtmlTags(
  html: string,
  prefix: string = 'custom',
): JSX.Element[] {
  return renderHtmlTags(html, prefix, 'lazyOnload');
}
